<template>
  <v-layout>
    <v-flex>
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid py-0>
            <v-row align="center">
              <div>
                <h1 class="headline" v-if="!forItinerary">
                  Purchase Invoice Payments
                </h1>
              </div>
              <div>
                <slot name="leftHeader"></slot>
              </div>
              <v-col class="py-0">
                <list-filters
                  :filters="filters"
                  label="Find Payment"
                  :ref-name="refName"
                  @searchParamsChanged="searchParamsChanged($event)"
                  :expansionPanel="false"
                ></list-filters>
              </v-col>
              <div class="pr-2">
                <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
              </div>
              <v-select
                v-if="showGroupedView"
                :items="groupByOptions"
                v-model="selectedGroupedBy"
                label="Group by"
                item-text="name"
                return-object
                :outlined="outlinedInputs"
                hide-details
              ></v-select>
              <v-switch
                prepend-icon="mdi-view-list"
                class="mr-3"
                label="Group View"
                v-model="showGroupedView"
              ></v-switch>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                ></list-visible-columns-and-filter>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
      <v-row v-if="showGroupedView">
        <v-col class="py-0">
          <list-grouped :group="selectedGroupedBy" :items="items">
            <template v-slot:table="{ items }">
              <v-card>
                <pi-payments-table
                  :items="items"
                  :headers="filteredHeaders"
                  :search="search"
                  @delete="deleteItem"
                  @edit="editItem"
                  @export-to-xero="onBatchExportToAccounts"
                ></pi-payments-table>
              </v-card>
            </template>
          </list-grouped>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col class="py-0">
          <div v-if="showAgGrid" key="ag-grid">
            <ag-grid-vue
              class="ag-theme-alpine"
              id="ag-pip-list"
              :columnDefs="agGridColumns"
              :rowData="items"
              rowSelection="multiple"
              :context="agContext"
              @grid-ready="onAgGridReady"
              @columnMoved="onAgColumnMoved"
              @columnResized="onAgColumnResized"
              @sortChanged="onAgSortChanged"
              @selection-changed="onAgSelectionChanged"
              @newColumnsLoaded="onAgNewColumns"
              suppressDragLeaveHidesColumns
            >
            </ag-grid-vue>
            <list-actions
              v-if="selected.length > 0"
              style="top: -30px; position: relative"
              :selected="selected"
              list="itineraries"
              :columns="visibleColumns"
              :filter="searchParams"
              :hideExport="hideExport"
              @close="selected = []"
            >
            </list-actions>
          </div>
          <v-data-table
            v-else
            :headers="filteredHeaders"
            :items="items"
            :loading="$apollo.loading"
            v-bind="pagingOptions"
            :search="search"
            item-key="id"
            class="elevation-1"
            v-model="selected"
            show-select
            fixed-header
            hide-default-footer
            :height="gridHeight"
            @update:options="onListOptionsUpdate"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            @current-items="dataTableReady"
          >
            <template v-slot:item.itineraryReference="{ item }">
              <reference-label
                v-model="item.itineraryReference"
                :itinerary-id="item.itinerary ? item.itinerary.id : ''"
                new-tab
              ></reference-label>
            </template>

            <template v-slot:item.supplierPayeeName="{ item }">
              {{
                item.invoice && item.invoice.supplier
                  ? item.invoice.supplier.payeeName
                  : ""
              }}
            </template>
            <template v-slot:item.supplierEmail="{ item }">
              {{
                item.invoice && item.invoice.supplier
                  ? item.invoice.supplier.email
                  : ""
              }}</template
            >
            <template v-slot:item.accountsEmail="{ item }">
              {{
                item.invoice && item.invoice.supplier
                  ? item.invoice.supplier.accountsEmail
                  : ""
              }}</template
            >
            <template v-slot:item.serviceStartDate="{ item }">
              {{
                item.invoice && item.invoice.firstServiceStartDate
                  ? item.invoice.firstServiceStartDate
                  : "" | formatDate
              }}
            </template>
            <template v-slot:item.amount="{ item }">{{
              item.amount | priceLabeler
            }}</template>
            <template v-slot:item.itineraryStartDate="{ item }">{{
              item.itineraryStartDate | formatDate
            }}</template>
            <template v-slot:item.exportedAt="{ item }">{{
              item.exportedAt | formatDate
            }}</template>
            <template v-slot:item.paymentDate="{ item }">{{
              item.paymentDate | formatDate
            }}</template>
            <template v-slot:item.createdAt="{ item }">{{
              item.createdAt | formatDate
            }}</template>
            <template v-slot:item.exported="{ item }">
              <v-checkbox
                v-model="item.exported"
                primary
                hide-details
                :disabled="true"
                class="ma-0"
              ></v-checkbox>
            </template>
            <template v-slot:item.posted="{ item }">
              <v-checkbox
                v-model="item.posted"
                primary
                hide-details
                :disabled="true"
                class="ma-0"
              ></v-checkbox>
            </template>
            <template v-slot:item.actions="{ item }">
              <default-list-actions
                :item="item"
                :hideView="true"
                :hideRemove="hideRemove"
                @deleteItem="deleteItem($event)"
                @viewItem="viewItem($event)"
                @editItem="editItem($event)"
              ></default-list-actions>
            </template>
            <template v-slot:footer>
              <list-actions
                v-if="
                  selected.length > 0 &&
                  !$store.getters.hasAccessTo('finances:pi_payments_view_only')
                "
                :selected="selected"
                list="purchaseInvoicePayments"
                :columns="visibleColumns"
                :filter="searchParams"
                show-sage
                :hideExport="hideExport"
                @update="onListUpdate"
                @close="selected = []"
                :customFormats="customExportFormats"
              >
                <v-btn
                  v-if="showXero"
                  fab
                  dark
                  small
                  color="blue darken-1"
                  @click="onBatchExportToAccounts(selected)"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">mdi-cloud-upload</v-icon>
                    </template>
                    Export to Accounts
                  </v-tooltip>
                </v-btn>
              </list-actions>
              <v-row justify="center" v-if="selected.length > 0">
                <h3 class="py-2 caption">
                  {{ selected.length }} record(s) selected
                </h3>
              </v-row>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="purchaseInvoicePayments"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('purchaseInvoicePayments')"
      >
      </get-more-results>
      <v-row
        ><list-totals v-model="totalsColumns" :items="items"></list-totals
      ></v-row>
    </v-flex>
    <basic-dialog :show="showDetailsDialog" @close="showDetailsDialog = false">
      <template v-if="selectedItem" v-slot:title>{{
        selectedItem.piNumber
      }}</template>
      <purchase-invoice-payment-details
        v-model="selectedItem"
        ref="details"
      ></purchase-invoice-payment-details>
      <template v-slot:actions>
        <v-btn text v-if="showQuickBooks" @click="onExportToQuickBooks"
          >Export to Quick Books</v-btn
        >
        <v-btn
          color="primary"
          @click="onSave"
          :disabled="
            $store.getters.hasAccessTo('finances:pi_payments_view_only')
          "
          >Save</v-btn
        >
      </template>
    </basic-dialog>
    <v-row v-if="debug">
      <v-col>
        {{ customExportFormats }}
      </v-col>
    </v-row>
    <task-status
      :show="showBatchExportTaskStatus"
      v-model="batchExportTask"
      @complete="onBatchExportToAccountsComplete"
    ></task-status>
  </v-layout>
</template>
<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import AgGridListMixin from "../../../../mixins/AgGridListMixin";
import {
  ALL_PURCHASE_INVOICE_PAYMENTS_SEARCH_QUERY,
  PURCHASE_INVOICE_PAYMENT_DELETE_MUTATION,
  EXPORT_PAYMENT_TO_QUICK_BOOKS_MUTATION,
} from "../../../../constants/graphql";
import GetMoreResults from "../../shared/GetMoreResults";
import DefaultListActions from "../../shared/DefaultListActions";
import ListFilters from "../../shared/list-filters/ListFilters";
import purchaseInvoicePaymentsFilters from "./purchase-invoice-payments-filters";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import {
  removeGQLTypename,
  sortByDate,
  sortByDateWithFormat,
  sortByFn,
  sortByReference,
} from "../../../../helpers/index";
import ListActions from "../../shared/list-actions/ListActions";
import ListGrouped from "../../shared/ListGrouped";
import PiPaymentsTable from "./PiPaymentsTable";
import BasicDialog from "../../shared/BasicDialog";
import PurchaseInvoicePaymentDetails from "./PurchaseInvoicePaymentDetails";
import ReferenceLabel from "../../shared/labels/ReferenceLabel";
import { AgGridVue } from "ag-grid-vue";
import AgGridSwitch from "../../shared/AgGridSwitch";
import ListTotals from "../../shared/ListTotals.vue";
import BatchExportSupplierPaymentsToAccountsMixin from "./batch-export-supplier-payments-to-accounts-mixin";
import TaskStatus from "../../shared/TaskStatus";
export default {
  name: "PurchaseInvoicePaymentList",
  mixins: [
    ListMixin,
    AgGridListMixin,
    BatchExportSupplierPaymentsToAccountsMixin,
  ],
  components: {
    GetMoreResults,
    DefaultListActions,
    ListFilters,
    ListVisibleColumnsAndFilter,
    ListActions,
    ListGrouped,
    PiPaymentsTable,
    BasicDialog,
    ReferenceLabel,
    PurchaseInvoicePaymentDetails,
    AgGridSwitch,
    AgGridVue,
    ListTotals,
    TaskStatus,
  },
  props: {
    customFilters: {
      type: Object,
    },
    itinerary: {
      type: Object,
      default: () => null,
    },
    cachedFilters: {
      type: Boolean,
      default: () => true,
    },
    refName: {
      type: String,
      default: () => "PurchaseInvoicePaymentList",
    },
    refNameColumns: {
      type: String,
      default: () => "PurchaseInvoicePaymentListColumns",
    },
  },
  data() {
    return {
      debug: false,
      selectedItem: null,
      showDetailsDialog: false,
      showGroupedView: false,
      selectedGroupedBy: {
        name: "Batch",
        getKey: (item) => item.batchNo,
        getLabel: (item) => `Batch No: ${item.batchNo}`,
      },
      groupByOptions: [
        {
          name: "Batch No",
          getKey: (item) => item.batchNo,
          getLabel: (item) => `Batch No: ${item.batchNo}`,
        },
        {
          name: "Payee",
          getKey: (item) => item.payeeName,
          getLabel: (item) => item.payeeName,
        },
        {
          name: "Payment ref",
          getKey: (item) => item.reference,
          getLabel: (item) => item.reference,
        },
      ],
      purchaseInvoicePayments: {},
      selected: [],
      purchaseInvoicePaymentsFilters: purchaseInvoicePaymentsFilters,
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "clientSurname",
        "supplierName",
        "itineraryReference",
        "amount",
        "currencyCode",
        "actions",
      ]),
      headers: [
        {
          text: "Client Name",
          value: "clientName",
          name: "clientName",
          sortable: true,
        },
        {
          text: "Client Surname",
          value: "clientSurname",
          name: "clientSurname",
          sortable: true,
        },
        {
          text: "Supplier",
          value: "supplierName",
          name: "supplierName",
          sortable: true,
        },
        {
          text: "Iti Ref",
          value: "itineraryReference",
          name: "itineraryReference",
          sortable: true,
          sort: sortByReference,
          comparator: sortByReference,
          cellRendererFramework: "ReferenceLabel",
        },
        {
          text: "Agent",
          value: "agentName",
          name: "agentName",
          sortable: true,
        },
        {
          text: "Created By",
          value: "createdByName",
          name: "createdByName",
          sortable: true,
        },
        {
          text: "Payment Reference",
          value: "reference",
          name: "reference",
          sortable: true,
        },
        {
          text: "Iti Start Date",
          value: "itineraryStartDate",
          name: "itineraryStartDate",
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          sortable: true,
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.itineraryStartDate),
        },
        {
          text: "Iti Department",
          value: "itineraryDepartment",
          sortable: false,
          name: "itineraryDepartment",
          sortable: true,
        },
        {
          text: "Iti Brand",
          value: "itineraryBrand",
          sortable: false,
          name: "itineraryBrand",
          sortable: true,
        },
        {
          text: "Iti Internal Status",
          value: "itineraryInternalStatus",
          name: "itineraryInternalStatus",
          sortable: true,
        },
        {
          text: "Amount",
          value: "amount",
          name: "amount",
          sortable: true,
          groupBy: "currencyCode",
        },
        {
          text: "Currency",
          value: "currencyCode",
          name: "currencyCode",
          sortable: true,
        },
        {
          text: "Exchange Rate",
          value: "exchangeRate",
          name: "exchangeRate",
          sortable: true,
        },
        {
          text: "Head Office",
          value: "headOfficeName",
          sortable: false,
          name: "headOfficeName",
        },
        {
          text: "Exported",
          value: "exported",
          name: "exported",
          sortable: true,
        },
        {
          text: "Exported By",
          value: "exportedByName",
          name: "exportedByName",
          sortable: true,
        },
        {
          text: "Exported At",
          value: "exportedAt",
          name: "exportedAt",
          sortable: true,
        },
        {
          text: "Posted",
          value: "posted",
          name: "posted",
          sortable: true,
        },
        {
          text: "Payment Date",
          value: "paymentDate",
          name: "paymentDate",
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          sortable: true,
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.paymentDate),
        },
        {
          text: "Service Start Date",
          value: "serviceStartDate",
          name: "serviceStartDate",
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          sortable: true,
          valueGetter: (payload) =>
            payload.data.invoice && payload.data.invoice.firstServiceStartDate
              ? this.$options.filters.formatDate(
                  payload.data.invoice.firstServiceStartDate
                )
              : "",
        },
        {
          text: "Type",
          value: "paymentType",
          name: "paymentType",
          sortable: true,
        },
        {
          text: "Payment Method",
          value: "paymentMethodName",
          name: "paymentMethodName",
          sortable: true,
        },
        {
          text: "Notes",
          value: "notes",
          name: "notes",
          sortable: true,
        },
        {
          text: "Created At",
          value: "createdAt",
          name: "createdAt",
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          sortable: true,
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.createdAt),
        },
        {
          text: "Inv. Number",
          value: "piNumber",
          name: "piNumber",
          sortable: true,
        },
        {
          text: "Supplier Invoice No",
          value: "externalReference",
          name: "externalReference",
          sortable: true,
        },
        {
          text: "Payee",
          value: "payeeName",
          name: "payeeName",
          sortable: true,
        },
        {
          text: "Payee Name",
          value: "supplierPayeeName",
          name: "supplierPayeeName",
          sortable: false,
          valueGetter: (payload) =>
            payload.data.invoice && payload.data.invoice.supplier
              ? payload.data.invoice.supplier.payeeName
              : "",
        },
        {
          text: "Supplier Email",
          value: "supplierEmail",
          name: "supplierEmail",
          sortable: false,
          valueGetter: (payload) =>
            payload.data.invoice && payload.data.invoice.supplier
              ? payload.data.invoice.supplier.email
              : "",
        },
        {
          text: "Accounts Email",
          value: "accountsEmail",
          name: "accountsEmail",
          sortable: false,
          valueGetter: (payload) =>
            payload.data.invoice && payload.data.invoice.supplier
              ? payload.data.invoice.supplier.accountsEmail
              : "",
        },
        {
          text: "Batch No",
          value: "batchNo",
          name: "batchNo",
          sortable: true,
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "DefaultListActions",
          valueGetter: (payload) => ({
            agGridViewOptions: {
              hideRemove:
                !this.$store.getters.hasAccessTo("finances:delete") ||
                this.$store.getters.hasAccessTo("finances:pi_view_only"),
              hideEdit: false,
            },
          }),
        },
        {
          text: "Batch No",
          value: "batchNo",
          name: "batchNo",
          sortable: true,
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "DefaultListActions",
          valueGetter: (payload) => ({
            agGridViewOptions: {
              hideRemove:
                !this.$store.getters.hasAccessTo("finances:delete") ||
                this.$store.getters.hasAccessTo("finances:pi_view_only"),
              hideEdit: false,
            },
          }),
        },
      ],
    };
  },

  computed: {
    showQuickBooks() {
      return this.$store.getters.hasAccessTo("finances:quick_books");
    },
    showXero() {
      return this.$store.getters.hasAccessTo("finances:xero_export");
    },
    customExportFormats() {
      // Revolut export available on all domains (#11092)
      const formats = [
        {
          text: "Revolut",
          value: "REVOLUT",
        },
      ];
      const domainsForBankExport = ["golfandtravel.ie", "ALAINNTOURS"];
      if (
        domainsForBankExport.includes(this.$store.getters.userConfig.domain)
      ) {
        formats.push(
          {
            text: "Bank Export EUR",
            value: "BANK_EXPORT_EUR",
          },
          {
            text: "Bank Export GBP",
            value: "BANK_EXPORT_GBP",
          }
        );
      }
      return formats;
    },
    totalsColumns() {
      const cols = ["amount"];
      return this.filteredHeaders.filter((h) => cols.indexOf(h.name) != -1);
    },
    hideExport() {
      return !this.$store.getters.hasAccessTo("finances:export");
    },

    hideRemove() {
      return (
        !this.$store.getters.hasAccessTo("finances:delete") ||
        this.$store.getters.hasAccessTo("finances:pi_payments_view_only")
      );
    },
    items() {
      return this.purchaseInvoicePayments && this.purchaseInvoicePayments.edges
        ? this.purchaseInvoicePayments.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return purchaseInvoicePaymentsFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    forItinerary() {
      return this.customFilters && this.customFilters.itinerary_id != "";
    },
    showPaymentMethods() {
      return this.$store.getters.hasAccessTo("finances:payment_methods");
    },
    selectableHeaders() {
      const baseHeaders = this.headers.filter(
        (h) => h.name != "menuActions"
      );
      const filtered = this.showPaymentMethods
        ? baseHeaders
        : baseHeaders.filter((h) => h.name !== "paymentMethodName");
      return filtered.sort(sortByFn((h) => h.name));
    },
    filteredHeaders() {
      const headers = this.showPaymentMethods
        ? this.headers
        : this.headers.filter((h) => h.name !== "paymentMethodName");
      const hs = [];
      for (const headerName of this.visibleColumns) {
        const h = headers.find((h) => h.name == headerName);
        if (h) {
          hs.push(h);
        }
      }
      return this.hideActions ? hs.filter((h) => h.name != "actions") : hs;
    },
  },

  methods: {
    onExportToQuickBooks() {
      this.$apollo
        .mutate({
          mutation: EXPORT_PAYMENT_TO_QUICK_BOOKS_MUTATION,
          variables: { id: this.selectedItem.id },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportPaymentToQuickBooks.message,
            color: data.exportPaymentToQuickBooks.success ? "success" : "error",
          });
        })
        .catch((err) => {
          console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onBatchExportToAccounts(list) {
      const records = list && list.length ? list : this.selected;
      this.batchExportToAccounts(records, "purchaseInvoicePayments", "xero");
    },
    onGetSageCSV() {
      console.log("getting csv");
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: PURCHASE_INVOICE_PAYMENT_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", {
        msg: "Purchase invoice payment removed.",
      });
      if (this.$apollo.queries.purchaseInvoicePayments != null) {
        this.$apollo.queries.purchaseInvoicePayments.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
    },

    editItem(item) {
      //this.$router.push("/purchaseinvoicepayment/" + item.id + "/details");
      this.selectedItem = { ...item };
      this.showDetailsDialog = true;
    },
    onSave() {
      this.$refs.details.savePayment();
    },
    onListUpdate() {
      //this.selected = []
      if (this.$apollo.queries.purchaseInvoicePayments != null) {
        this.$apollo.queries.purchaseInvoicePayments.refetch();
      }
    },
  },

  apollo: {
    purchaseInvoicePayments: {
      query: ALL_PURCHASE_INVOICE_PAYMENTS_SEARCH_QUERY,
      variables() {
        return {
          first: this.first,
          after: this.after,
          filter: {
            ...this.customFilters,
            ...this.searchParams,
          },
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.purchaseInvoicePayments.totalCount;
      },
    },
  },
};
</script>
<style scoped>
#ag-pip-list {
  height: calc(100vh - 500px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/finances/purchase-invoice-payments/PurchaseInvoicePaymentList.vue